import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheck,
  FiClock,
  FiCopy,
  FiList,
  FiTrendingUp,
} from "react-icons/fi";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import { getAllBlogs, getBlogById } from "../../../../services/operations/blogApi";
import { formattedDate } from "../../../../utils/dateFormatter";
import Footer from "../../../../common/Footer";
import "./Blogs.css";
import { blogEndpoints } from "../../../../services/apis";

const InlineContent = ({ text }) => {
  const parts = `${text}`.split(
    /(\*\*[^*]+?\*\*|__[^_]+?__|\*[^*]+?\*|\[[^\]]+?\]\([^)]+?\))/g
  );

  return parts.map((part, index) => {
    if (/^\*\*.+\*\*$/.test(part)) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (/^__.+__$/.test(part)) {
      return <u key={index}>{part.slice(2, -2)}</u>;
    }
    if (/^\*.+\*$/.test(part)) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const safeUrl = /^https?:\/\//i.test(link[2]) ? link[2] : "#";
      return (
        <a key={index} href={safeUrl} target="_blank" rel="noreferrer">
          {link[1]}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const BlogDetails = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      const [res, allBlogs] = await Promise.all([
        getBlogById(blogId),
        getAllBlogs(),
      ]);
      if (res) {
        setBlog(res);
        if (res.slug && res.slug !== blogId) {
          navigate(`/blogs/${res.slug}`, { replace:true });
        }
      }
      setLatestBlogs(
        (Array.isArray(allBlogs) ? allBlogs : [])
          .filter((item) => item._id !== blogId)
          .slice(0, 3)
      );
    };
    fetchBlog();
  }, [blogId, navigate]);

  useEffect(() => {
    const updateProgress = () => {
      const available = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(available > 0 ? (window.scrollY / available) * 100 : 0);
    };
    window.addEventListener("scroll", updateProgress, { passive: true });
    return () => window.removeEventListener("scroll", updateProgress);
  }, []);

  const blocks = useMemo(() => {
    if (!blog?.content) return [];
    const sections = `${blog.content}`
      .split(/\n\s*\n/)
      .map((section) => section.trim())
      .filter(Boolean);
    const parsed = [];
    let index = 0;

    while (index < sections.length) {
      const section = sections[index];
      const headingMatch = section.match(/^(#{1,3})\s+(.+)/);
      const bulletLines = section
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      const isBulletSection = bulletLines.every((line) =>
        /^[-*•]\s+/.test(line)
      );
      const isNumberedSection = bulletLines.every((line) =>
        /^\d+[.)]\s+/.test(line)
      );
      const nextSection = sections[index + 1] || "";
      const wordCount = section.split(/\s+/).length;
      const nextWordCount = nextSection.split(/\s+/).filter(Boolean).length;
      const looksLikeHeading =
        wordCount <= 12 &&
        nextWordCount > 8 &&
        !/[.!?,:;]$/.test(section) &&
        !section.startsWith("#");

      if (headingMatch) {
        const text = headingMatch[2];
        parsed.push({ type: "heading", text, id: text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") });
      } else if (looksLikeHeading) {
        parsed.push({
          type: "heading",
          text: section,
          id: section
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, ""),
        });
      } else if (isBulletSection) {
        parsed.push({
          type: "list",
          items: bulletLines.map((line) => line.replace(/^[-*•]\s+/, "")),
        });
      } else if (isNumberedSection) {
        parsed.push({
          type: "list",
          ordered: true,
          items: bulletLines.map((line) => line.replace(/^\d+[.)]\s+/, "")),
        });
      } else if (/^>\s+/.test(section)) {
        parsed.push({ type: "quote", text: section.replace(/^>\s+/, "") });
      } else if (section.endsWith(":")) {
        const items = [];
        let itemIndex = index + 1;
        while (itemIndex < sections.length) {
          const candidate = sections[itemIndex];
          const candidateWords = candidate.split(/\s+/).length;
          if (
            candidateWords > 8 ||
            candidate.includes("\n") ||
            /[.!?,:;]$/.test(candidate)
          ) {
            break;
          }
          items.push(candidate);
          itemIndex += 1;
        }
        parsed.push({ type: "paragraph", text: section });
        if (items.length >= 2) {
          parsed.push({ type: "list", items });
          index = itemIndex - 1;
        }
      } else {
        parsed.push({
          type: "paragraph",
          text: section.replace(/\r?\n/g, " "),
        });
      }
      index += 1;
    }
    return parsed;
  }, [blog]);

  if (!blog)
    return (
      <div className="blog-detail-loading">
        <span />
        <p>Preparing your article…</p>
      </div>
    );

  const category =
    blog?.category?.name || blog?.category || blog?.topic || "Market Insights";
  const wordCount = `${blog.content || ""}`.trim().split(/\s+/).length;
  const readTime = Math.max(2, Math.ceil(wordCount / 210));
  const contentImages = Array.isArray(blog.contentImages)
    ? blog.contentImages
    : [];
  const renderImages = (afterBlock) =>
    contentImages
      .filter((image) => Number(image.afterBlock || 0) === afterBlock)
      .map((image) => (
        <figure
          key={image._id || `${image.url}-${afterBlock}`}
          className={`blog-inline-image blog-inline-image--${
            image.alignment || "center"
          }`}
        >
          <img src={image.url} alt={image.caption || blog.title} />
          {image.caption && <figcaption>{image.caption}</figcaption>}
        </figure>
      ));
  const trailingImages = contentImages.filter(
    (image) => Number(image.afterBlock || 0) > blocks.length
  );
  const pageUrl = `${window.location.origin}/blogs/${blog.slug || blog._id}`;
  const socialShareUrl = `${blogEndpoints.GET_BLOG_SHARE}/${encodeURIComponent(blog.slug || blog._id)}`;
  const encodedSocialUrl = encodeURIComponent(socialShareUrl);
  const encodedTitle = encodeURIComponent(blog.title);
  const shareLinks = [
    {
      name: "WhatsApp",
      icon: <FaWhatsapp />,
      href: `https://wa.me/?text=${encodedTitle}%20${encodedSocialUrl}`,
    },
    {
      name: "LinkedIn",
      icon: <FaLinkedinIn />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedSocialUrl}`,
    },
    {
      name: "Facebook",
      icon: <FaFacebookF />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedSocialUrl}`,
    },
    {
      name: "X",
      icon: <FaXTwitter />,
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedSocialUrl}`,
    },
  ];

  const copyLink = async () => {
    await navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <>
      <div className="blog-reading-progress" style={{ width: `${progress}%` }} />
      <main className="blog-detail">
        <header className="blog-detail-header">
          <div className="blog-detail-copy">
            <Link to="/blogs" className="blog-back"><FiArrowLeft /> All articles</Link>
            <p className="blog-detail-category">{category}</p>
            <h1>{blog.title}</h1>
            <p className="blog-detail-summary">{blog.summary}</p>
            <div className="blog-detail-meta">
              <span>CNT Academy Editorial</span>
              <time dateTime={blog.createdAt}>{formattedDate(blog.createdAt)}</time>
              <span><FiClock /> {readTime} min read</span>
            </div>
          </div>
        </header>

        <article className="blog-article">
          <img className="blog-cover" src={blog.thumbnail} alt={blog.title} />
          <div className="blog-article-layout">
            <aside className="blog-article-sidebar">
              {blocks.some((block) => block.type === "heading") && (
                <div className="blog-toc">
                  <h2><FiList /> In this article</h2>
                  {blocks.filter((block) => block.type === "heading").map((heading) => (
                    <a key={heading.id} href={`#${heading.id}`}>{heading.text}</a>
                  ))}
                </div>
              )}
              {latestBlogs.length > 0 && (
                <div className="blog-latest">
                  <h2><FiTrendingUp /> Latest insights</h2>
                  {latestBlogs.map((item) => (
                    <Link key={item._id} to={`/blogs/${item.slug || item._id}`}>
                      <img src={item.thumbnail} alt="" />
                      <span>{item.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </aside>
            <div className="blog-prose">
              {renderImages(0)}
              {blocks.map((block, index) => {
                let contentBlock;
                if (block.type === "heading") contentBlock = <h2 id={block.id}><InlineContent text={block.text} /></h2>;
                else if (block.type === "list") {
                  const ListTag = block.ordered ? "ol" : "ul";
                  contentBlock = <ListTag>{block.items.map((item) => <li key={item}><InlineContent text={item} /></li>)}</ListTag>;
                }
                else if (block.type === "quote") contentBlock = <blockquote><InlineContent text={block.text} /></blockquote>;
                else contentBlock = <p><InlineContent text={block.text} /></p>;
                return (
                  <React.Fragment key={index}>
                    {contentBlock}
                    {renderImages(index + 1)}
                  </React.Fragment>
                );
              })}
              {trailingImages.map((image) => (
                <figure
                  key={image._id || image.url}
                  className={`blog-inline-image blog-inline-image--${
                    image.alignment || "full"
                  }`}
                >
                  <img src={image.url} alt={image.caption || blog.title} />
                  {image.caption && <figcaption>{image.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </div>
          <section className="blog-share" aria-label="Share this article">
            <div>
              <p>Found this useful?</p>
              <h2>Share this article</h2>
            </div>
            <div className="blog-share-buttons">
              {shareLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Share on ${link.name}`}
                  title={`Share on ${link.name}`}
                >
                  {link.icon}
                  <span>{link.name}</span>
                </a>
              ))}
              <button type="button" onClick={copyLink}>
                {copied ? <FiCheck /> : <FiCopy />}
                <span>{copied ? "Copied" : "Copy link"}</span>
              </button>
            </div>
          </section>
          <div className="blog-article-end">
            <p>Keep learning with CNT Academy</p>
            <Link to="/blogs">Explore more articles <FiArrowLeft /></Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

export default BlogDetails;
